/**
 * Every node the chain's telemetry feed reports.
 *
 * The point of this page is that it has no entry condition. A node reaches the
 * miner route only through the first-reporter voting — it has to have won
 * blocks *and* had the feed resolve a clean lead on enough of them — and most
 * nodes never mine at all. So the one door we had admitted almost nobody, while
 * the feed was handing us every node on the chain unasked.
 *
 * Live rows first. A node the feed is reporting right now is a different claim
 * from one we last heard about at some point in the past, and the `live` column
 * says which without the reader having to work it out from a timestamp.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import type { NodeIndex } from '../api/generated/NodeIndex'
import { RequestFailed, fetchNodes } from '../api/rest'
import { ago, bytes, height as fmtHeight } from '../lib/format'
import { href } from '../lib/routes'

/** A two-letter country code as its flag. Null for anything else, so a
 *  malformed code renders as nothing rather than as tofu. */
function flag(code: string | null): string | null {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return null
  const base = 0x1f1e6 - 'A'.charCodeAt(0)
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => base + c.charCodeAt(0)))
}

export function NodesIndex({ chain }: { chain: string }) {
  const [index, setIndex] = useState<NodeIndex | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setIndex(null)
    setError(null)
    fetchNodes(chain, controller.signal)
      .then(setIndex)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  // Name, peer id and CPU all together: "find my node" is the question this
  // page exists to answer, and a reader may remember any of the three.
  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    const all = index?.nodes ?? []
    if (!needle) return all
    return all.filter((n) =>
      [n.name, n.peer_id, n.cpu, n.version, n.country_code]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(needle)),
    )
  }, [index, filter])

  const liveCount = index?.nodes.filter((n) => n.live).length ?? 0

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Nodes</h2>
        {index && (
          <span className="eyebrow">
            {liveCount} reporting now
            {index.reported !== null && ` · ${fmtHeight(index.reported)} on telemetry`}
          </span>
        )}
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && index === null && <p className="empty">Asking telemetry…</p>}

      {index !== null && (
        <>
          <div className="pager" style={{ borderTop: 0, borderBottom: 'var(--rule)' }}>
            <input
              className="node-filter"
              type="search"
              value={filter}
              placeholder="Filter by name, peer id, CPU, version or country"
              aria-label="Filter nodes"
              onChange={(e) => setFilter(e.target.value)}
            />
            {filter && (
              <span className="eyebrow">
                {rows.length} of {index.nodes.length}
              </span>
            )}
          </div>

          <div className="scroll-x">
            <table className="board">
              <caption className="visually-hidden">
                Every node this chain&apos;s telemetry feed reports
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="left">
                    Node
                  </th>
                  <th scope="col">Country</th>
                  <th scope="col" className="left">
                    Version
                  </th>
                  <th scope="col" className="left">
                    CPU
                  </th>
                  <th scope="col">Cores</th>
                  <th scope="col">Memory</th>
                  <th scope="col">Peers</th>
                  <th scope="col" className="event-when">
                    Heard
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((n) => (
                  <tr key={n.peer_id} className={n.live ? undefined : 'call-unused'}>
                    <td className="left">
                      <div className="call-cell">
                        <Link to={href({ chain, node: n.peer_id })}>{n.name}</Link>
                        <span className="runtime-type">{n.peer_id}</span>
                      </div>
                    </td>
                    <td title={n.country_code ?? 'not located'}>
                      {n.country_code ? `${flag(n.country_code) ?? ''} ${n.country_code}` : '—'}
                    </td>
                    <td className="left numeral">{n.version ?? '—'}</td>
                    <td className="left">{n.cpu ?? '—'}</td>
                    <td className="numeral">{n.core_count ?? '—'}</td>
                    <td className="numeral">{n.memory !== null ? bytes(n.memory) : '—'}</td>
                    <td className="numeral">{n.peer_count ?? '—'}</td>
                    <td className="numeral event-when" style={{ color: 'var(--text-muted)' }}>
                      {n.live ? 'now' : n.seen_at ? ago(n.seen_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="panel-note">
            Everything here is what each node reports about itself on the telemetry feed — never
            verified, and never a claim the chain makes. A node is listed whether or not it has ever
            won a block, which is the difference between this page and the standings. Rows shown
            faintly are from this observer&apos;s cache: the feed is not reporting them right now,
            so their peer counts are absent rather than stale. Location is resolved to a country and
            no finer, because the feed&apos;s coordinates come from IP geolocation and are
            unreliable at city resolution.
          </p>
        </>
      )}
    </section>
  )
}
